import { useState } from "react";
import { Check, CreditCard, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "../_shared";

/**
 * Lets an admin bypass Stripe and activate (or extend) a membership /
 * archive subscription for the user being viewed. Audit-logged on the
 * backend. The grant adds to whatever's left of the current period, never
 * shortens it.
 */
export default function GrantSubscriptionDialog({ userId, userEmail, onGranted, onClose }) {
  const [plan, setPlan] = useState("membership");
  const [months, setMonths] = useState(3);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(
        `/admin/users/${userId}/subscription/grant`,
        { plan, months, note },
      );
      toast.success(
        `Granted ${months}-month ${plan} to ${userEmail}`,
      );
      onGranted?.(data);
      onClose();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to grant subscription.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4"
      data-testid="grant-sub-dialog"
    >
      <Card className="w-full max-w-md p-6">
        <header className="flex items-start gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
            <Sparkles className="h-4 w-4" />
          </span>
          <div>
            <h2 className="text-base font-medium text-slate-900">
              Grant subscription
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Bypass Stripe and activate a plan for {userEmail}. Extends the
              current period; never shortens it. Audit-logged.
            </p>
          </div>
        </header>

        <div className="mt-5 grid grid-cols-2 gap-3">
          {[
            { key: "membership", label: "Membership", icon: CreditCard },
            { key: "archive", label: "Archive", icon: Sparkles },
          ].map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPlan(p.key)}
              className={`inline-flex flex-col items-center gap-1 rounded-xl border px-3 py-3 text-sm ${
                plan === p.key
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
              data-testid={`grant-sub-plan-${p.key}`}
            >
              <p.icon className="h-4 w-4" />
              {p.label}
            </button>
          ))}
        </div>

        <label className="mt-4 block">
          <span className="block text-xs font-medium text-slate-700 mb-1">
            Months to grant (1-24)
          </span>
          <input
            type="number"
            min={1}
            max={24}
            value={months}
            onChange={(e) => setMonths(Number(e.target.value || 1))}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            data-testid="grant-sub-months"
          />
        </label>

        <label className="mt-4 block">
          <span className="block text-xs font-medium text-slate-700 mb-1">
            Reason (saved to audit log)
          </span>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. VIP comp · refund · beta tester"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
            data-testid="grant-sub-note"
          />
        </label>

        <footer className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            data-testid="grant-sub-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving || !plan || months < 1}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid="grant-sub-confirm"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Activate &amp; bypass payment
          </button>
        </footer>
      </Card>
    </div>
  );
}
